import {Injectable} from "@angular/core";
import {Feedback, Form} from "../repository/entity/feedback.entity";
import {FeedbackRepository} from "../repository/feedback.repository";
import {BehaviorSubject, filter, firstValueFrom, lastValueFrom, Observable, of, switchMap, tap} from "rxjs";
import {NavSection, Section} from "./dto/section";
import {AbstractControlOptions, FormArray, FormControl, FormGroup, Validators} from "@angular/forms";

@Injectable({
  providedIn: 'root'
})
export class FeedbackService {

  private _sections = new BehaviorSubject<Section[]>([]);
  private _feedback = new BehaviorSubject<Feedback>(<Feedback>{});
  private _sectionForm = new BehaviorSubject<{ [key: string]: Form[] }>({[""]: []});

  constructor(private _feedbackRepo: FeedbackRepository) {
  }

  get sections(): Observable<Section[]> {
    return this._sections
  }

  set sections(value: Section[]) {
    this._sections.next(value)
  }

  get feedback(): Observable<Feedback> {
    return this._feedback;
  }

  set feedback(value: Feedback) {
    this._feedback.next(value);
  }

  get sectionForm(): Observable<{ [key: string]: Form[] }> {
    return this._sectionForm;
  }

  set sectionForm(value: { [key: string]: Form[] }) {
    this._sectionForm.next(value);
  }

  fetchFeedback(companyCode: string, refNum: string) {
    return this._feedbackRepo.getFeedback(companyCode, refNum).pipe(
      switchMap((data) => {
        return of(data.result)
      }),
      tap((data: Feedback) => {
        this.feedback = data;
      }),
      tap((data: Feedback) => {
        const forms = data.forms
        if (forms) {
          const sectionForm = this.groupByForm(forms, 'section');
          this.sectionForm = sectionForm

          this.sections = Object.keys(sectionForm).map((section, i: number) => {
            const navSection = this.generateNavSection(sectionForm, Object.keys(sectionForm)[i])
            return {
              isActive: i == 0,
              isCompleted: false,
              currentSection: navSection.current,
              nextSection: navSection.next,
              prevSection: navSection.prev
            }
          })
        }
      })
    );
  }

  private groupByForm(array: Form[], property: keyof Form) {
    return array.reduce((acc, obj) => {
      const key = obj[property] as string;
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(obj);
      return acc;
    }, {} as { [key: string]: Form[] });
  }

  async navigateSection(selectedSection: string) {
    let sections = await firstValueFrom(this.sections)
    sections = sections.map((section) => {
      section.isActive = section.currentSection == selectedSection;
      return section
    })
    this.sections = sections
  }

  async markSectionCompleted(selectedSection: string) {
    let sections = await firstValueFrom(this.sections)
    sections = sections.map((section) => {
      if (section.currentSection == selectedSection) {
        section.isCompleted = true;
      }
      return section
    })
    this.sections = sections
  }

  generateNavSection(arr: { [key: string]: Form[] }, value: string): NavSection {
    const arrSection = Object.keys(arr);
    const index = arrSection.indexOf(value);
    let next = null;
    let prev = null;
    if (index < arrSection.length - 1) {
      next = arrSection[index + 1]
    }

    if (index > 0) {
      prev = arrSection[index - 1]
    }

    return {
      prev: prev,
      current: value,
      next: next
    };

  }

  buildFormGroup(feedback: Feedback): { [index: string]: FormControl } {
    let formGroup: { [index: string]: any } = {};
    if (feedback.forms && feedback.forms.length > 0) {
      feedback.forms.forEach(form => {
        switch (form.type) {
          case "checkbox":
            let childFormGroup: { [index: string]: FormControl } = {}
            form.items.forEach((formItem) => {
              childFormGroup[formItem.id] = new FormControl()
              if (formItem.isAllowOtherValue) {
                childFormGroup["otherValue"] = new FormControl()
              }
            })
            formGroup[form.formControlName] = new FormGroup({
              id: new FormControl(form.id),
              section: new FormControl(form.section),
              mode: new FormControl("form"),
              value: new FormGroup(childFormGroup, {validators: this.validateCheckboxes}),
              typeForm: new FormControl(form.type)
            })
            break
          case "sales_menu":
            formGroup[form.formControlName] = new FormGroup({
              id: new FormControl(form.id),
              section: new FormControl(form.section),
              mode: new FormControl("sales"),
              value: new FormControl('', form.isRequired ? [Validators.required] : null),
              images: new FormControl(),
              otherValue: new FormControl(),
              typeForm: new FormControl(form.type)
            })
            break
          case "email":
          case "phone":
            let validator = [];
            if (form.type == "email") {
              validator.push(Validators.email)
            }
            if (form.type == "phone") {
              validator.push(Validators.pattern('[- +()0-9]{10,14}'))
            }
            if (form.isRequired) {
              validator.push(Validators.required)
            }
            formGroup[form.formControlName] = new FormGroup({
              id: new FormControl(form.id),
              section: new FormControl(form.section),
              mode: new FormControl("form"),
              value: new FormControl('', validator),
              otherValue: new FormControl(),
              typeForm: new FormControl(form.type)
            })
            break
          case "radio":
            formGroup[form.formControlName] = new FormGroup({
              id: new FormControl(form.id),
              section: new FormControl(form.section),
              mode: new FormControl("form"),
              value: new FormControl('', form.isRequired ? [Validators.required] : null),
              otherValue: new FormControl(),
              typeForm: new FormControl(form.type),
              items: new FormControl(form.items)
            })
            break
          default:
            formGroup[form.formControlName] = new FormGroup({
              id: new FormControl(form.id),
              section: new FormControl(form.section),
              mode: new FormControl("form"),
              value: new FormControl('', form.isRequired ? [Validators.required] : null),
              otherValue: new FormControl(),
              typeForm: new FormControl(form.type)
            })
            break
        }
      });
    }
    return formGroup
  }

  validateCheckboxes(control: AbstractControlOptions): Validators | null {
    const formGroup = control as FormGroup;
    const checkedBoxes = Object.values(formGroup.controls).some(control => control.value === true);
    return checkedBoxes ? null : {required: true};
  }

  async submitFeedback(companyCode: string, refNum: string, formGroup: FormGroup) {
    const feedback = await firstValueFrom(this.feedback)
    let finalFeedback = this.assignFormToFeedback(feedback, formGroup)
    return lastValueFrom(this._feedbackRepo.submitFeedback(companyCode, refNum, finalFeedback).pipe(
      filter((data) => data.code == 200),
      switchMap((data) => {
        return of (data.result)
      }),
      tap((feedback) => {
        this.feedback = feedback
      })
    ))
  }

  assignFormToFeedback(feedback: Feedback, formGroup: FormGroup): Feedback {
    if (feedback.forms) {
      feedback.forms = feedback.forms.filter((f) => f.type !== 'sales_menu')
      feedback.forms.map((form) => {
        let findFg = formGroup.get(form.formControlName) as FormArray
        let value = findFg.get("value")?.value
        let otherValue = findFg.get("otherValue")?.value
        form.value = value
        if (form.items && form.items.length) {
          form.items.map((item) => {
            item.isSelected = item.value == value
            item.otherValue = otherValue
            return item;
          })
        }
        return form;
      });
    }

    if (feedback.salesMenu) {
      feedback.salesMenu.map((sm) => {
        let findFg = formGroup.get("sales-" + sm.salesMenuId) as FormArray
        let value = findFg.get("value")?.value
        let otherValue = findFg.get("otherValue")?.value
        let attachment = findFg.get("images")?.value
        sm.rating = value
        sm.feedback = otherValue
        sm.attachment = attachment
        return sm;
      })
    }
    return feedback
  }
}
